import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  inputFields,
  selectFields,
  secondaryFields,
  commercialFields,
  landAndHouseFields,
  luxuryFields,
  parkingFields,
} from "../../../data/Fields";
import { createProperty } from "../model/addObjectSlice";
import s from "../styles.module.css";
import CustomInput from "../../../shared/components/CustomInput";
import AddObjectSelect from "../../../shared/components/Select/AddObjectSelect/AddObjectSelect";
import { toast } from "react-toastify";
import { createApi } from "@reduxjs/toolkit/query";
import { useNavigate } from "react-router-dom";
import PhotoUploader from "../../../shared/components/photoUploader/PhotoUploader";
import MapComponents from "../../../shared/components/MapComponents/MapComponents";
import useAuth from "../../../hooks/useAuth";

const AddObject = () => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.addObject);
  const { auth } = useAuth();
  const token = auth?.access_token;

  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);

  const {
    register,
    watch,
    setValue,
    reset,
    getValues,
    handleSubmit,
    formState: { errors },
    unregister,
  } = useForm({
    defaultValues: { propertyType: "" },
  });

  const [data, setData] = useState({});
  const [loadingData, setLoadingData] = useState(false);
  const [loadingError, setLoadingError] = useState(null);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [photoError, setPhotoError] = useState("");
  const [mapError, setMapError] = useState("");
  const navigate = useNavigate();

  const fieldsMap = {
    Коммерческая: commercialFields,
    Вторичная: secondaryFields,
    "Дома и участки": landAndHouseFields,
    Элитка: luxuryFields,
    Парковка: parkingFields,
  };

  const propertyType = watch("property_type");

  const handlePropertyTypeChange = useCallback(
    (newType) => {
      Object.keys(getValues()).forEach((key) => {
        if (key !== "property_type") {
          setValue(key, "");
        }
      });

      setValue("property_type", newType);
    },
    [getValues, setValue],
  );

  useEffect(() => {
    if (propertyType) {
      handlePropertyTypeChange(propertyType);
    }
  }, [propertyType, handlePropertyTypeChange]);

  const fetchData = async () => {
    setLoadingData(true);
    setLoadingError(null);
    try {
      const results = await Promise.all(
        [...selectFields, ...luxuryFields]
          .filter((field) => field.apiKey)
          .map(async (field) => {
            let url = "";

            if (field.apiKey === "district_id") {
              url = `${process.env.REACT_APP_API_URL}/api/v1/districts`;
            } else if (field.apiKey === "agents") {
              url = `${process.env.REACT_APP_API_URL}/api/v1/agents`;
            } else if (field.apiKey === "rooms") {
              url = `${process.env.REACT_APP_API_URL}/api/v1/rooms/`;
            } else if (
              field.apiKey === "residentials" &&
              luxuryFields.some((f) => f.apiKey === "residentials")
            ) {
              url = `${process.env.REACT_APP_API_URL}/api/v1/residentials/get-all`;
            } else {
              url = `${process.env.REACT_APP_API_URL}/api/v1/enums/${field.apiKey}`;
            }

            const response = await fetch(url);
            if (!response.ok) {
              throw new Error(`Ошибка: ${response.statusText}`);
            }

            let data = await response.json();

            if (field.apiKey === "district_id") {
              data = data.map((region) => ({
                title: region.name,
                districts: region.Districts.map((district) => ({
                  value: district.ID,
                  label: district.name,
                })),
              }));
            }

            if (field.apiKey === "residentials") {
              const residentialData = data.map((residential) => ({
                title: residential.name,
                residential: residential.Residential.map((residentialItem) => ({
                  value: residentialItem.ID,
                  label: residentialItem.name,
                })),
              }));

              if (luxuryFields.some((f) => f.apiKey === "residentials")) {
                return { [field.apiKey]: residentialData || [] };
              } else {
                return { [field.apiKey]: [] };
              }
            }

            return { [field.apiKey]: data || [] };
          }),
      );

      const additionalFieldResults = await Promise.all(
        [
          ...commercialFields,
          ...secondaryFields,
          ...landAndHouseFields,
          ...luxuryFields,
          ...parkingFields,
        ]
          .filter((field) => field.apiKey)
          .map(async (field) => {
            let url = "";
            console.log(field.apiKey);
            if (field.apiKey === "residentials") {
              url = `${process.env.REACT_APP_API_URL}/api/v1/residentials/get-all`;
            } else if (field.apiKey === "rooms") {
              url = `${process.env.REACT_APP_API_URL}/api/v1/rooms/`;
            } else {
              url = `${process.env.REACT_APP_API_URL}/api/v1/enums/${field.apiKey}`;
            }

            const response = await fetch(url);
            if (!response.ok) {
              throw new Error(`Ошибка: ${response.statusText}`);
            }
            const data = await response.json();

            return { [field.apiKey]: data || [] };
          }),
      );

      const combinedResults = results.concat(additionalFieldResults);
      setData(combinedResults.reduce((acc, item) => ({ ...acc, ...item }), {}));
    } catch (error) {
      console.error("Ошибка при загрузке данных:", error);
      setLoadingError(error.message);
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const onSubmit = async (formData) => {
    const propertyType = getValues("property_type");
    console.log(propertyType);
    if (!propertyType) {
      toast.error("Ошибка: Тип недвижимости не выбран!");
      return;
    }

    const landType = getValues("land_type");
    console.log(landType);
    if (landType === "Участок") {
      // Если area нет, отправляем 0 или пустую строку
      if (!formData.area) {
        delete formData.area;
      }
    }

    const selectedAgents = getValues("agents");
    if (!selectedAgents || selectedAgents.length === 0) {
      toast.error("Ошибка: Агенты не выбраны!");
      return;
    }

    if (selectedPhotos.length === 0) {
      toast.error("Добавьте хотя бы одно фото!");
      setPhotoError("Добавьте хотя бы одно фото!");
      return;
    } else {
      setPhotoError("");
    }

    if (!latitude || !longitude) {
      toast.error("Выберите местоположение на карте!");
      setMapError("Выберите местоположение на карте!");
      return;
    } else {
      setMapError("");
    }

    const formDataToSend = new FormData();

    Object.entries(formData).forEach(([key, value]) => {
      if (key === "agents") {
        value.forEach((agentId) => formDataToSend.append("agents[]", agentId));
      } else if (key === "document_type") {
        value.forEach((documentId) =>
          formDataToSend.append("documents[]", documentId),
        );
      } else {
        formDataToSend.append(key, value);
      }
    });

    if (selectedPhotos.length > 0) {
      selectedPhotos.forEach((file) => {
        formDataToSend.append("photos", file);
      });
    }

    if (latitude !== null) {
      formDataToSend.append("latitude", latitude);
    }
    if (longitude !== null) {
      formDataToSend.append("longitude", longitude);
    }

    try {
      const response = await dispatch(
        createProperty({ data: formDataToSend, propertyType, token, navigate }),
      );
      if (response.error) {
        console.log(response.error);
        throw new Error(response.error || "Ошибка при добавлении объекта!");
      }
      reset();
      setLatitude(null);
      setLongitude(null);
      setSelectedPhotos([]);
      setPhotoError("");
      setMapError("");

      toast.success("Объект успешно добавлен!");

      navigate("/myProperties");
    } catch (error) {
      console.log(error);
      console.error("Error:", error.error);
      toast.error(error.error || "Ошибка при добавлении объекта!");
    }
  };

  const landType = watch("land_type");

  useEffect(() => {
    if (landType === "Участок") {
      unregister("rooms");
      unregister("furniture");
      unregister("area");
    }
  }, [landType]);
  const renderField = (field, data) => {
    if (
      landType === "Участок" &&
      ["rooms", "furniture", "area"].includes(field.name)
    ) {
      return null;
    }

    if (field.type === "select") {
      let options = [];
      if (field.selectOptions) {
        options = field.selectOptions;
      } else if (field.apiKey && data[field.apiKey]) {
        // Старая логика с бэкендом
        if (Array.isArray(data[field.apiKey])) {
          if (field.apiKey === "agents") {
            options = data[field.apiKey].map((item) => ({
              value: item.id,
              label: item.name,
            }));
          } else if (field.apiKey === "document_type") {
            options = data[field.apiKey].map((item) => ({
              value: item.ID,
              label: item.name,
            }));
          } else if (field.apiKey === "district_id") {
            options = data[field.apiKey];
          } else if (field.apiKey === "residentials") {
            options = data[field.apiKey];
          } else {
            options = data[field.apiKey].map((item) => ({
              value: item.name,
              label: item.ID,
            }));
          }
        } else if (data[field.apiKey].data) {
          options = Object.entries(data[field.apiKey].data).map(
            ([key, value]) => ({
              value,
              label: key,
            }),
          );
        }
      }

      return (
        <div key={field.name}>
          <AddObjectSelect
            name={field.name}
            label={field.label}
            placeholder={field.placeholder}
            selected={watch(field.name)}
            {...register(field.name, field.validation)}
            setSelected={(value) =>
              setValue(
                field.name,
                field.apiKey === "agents" || field.apiKey === "document_type"
                  ? Array.isArray(value)
                    ? value
                    : [value]
                  : value,
                { shouldValidate: true },
              )
            }
            options={options}
            apiKey={field.apiKey}
            error={errors[field.name]}
            helperText={errors[field.name]?.message}
          />
        </div>
      );
    } else {
      return (
        <CustomInput
          key={field.name}
          name={field.name}
          label={field.label}
          placeholder={field.placeholder}
          type={field.type}
          {...register(field.name, field.validation)}
          error={!!errors[field.name]}
          helperText={errors[field.name]?.message}
        />
      );
    }
  };

  const renderDynamicFields = () => {
    const fields = fieldsMap[propertyType] || [];
    return fields
      .filter((field) => field.name !== "land_type")
      .map((field) => renderField(field, data));
  };

  if (loadingData) {
    return (
      <div className="loadingContainer">
        <div className="loader"></div>
      </div>
    );
  }
  if (loadingError) {
    return (
      <div className="errorDataBox">
        <p>{loadingError}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={s.formContainer}>
      <div className="container">
        <h1 className="title" style={{ marginBottom: 20 }}>
          Добавить объект
        </h1>
        <div className={s.row} style={{ marginBottom: 20 }}>
          {/* Тип недвижимости */}
          {renderField(
            selectFields.find((f) => f.name === "property_type"),
            data,
          )}

          {propertyType === "Дома и участки" &&
            renderField(
              landAndHouseFields.find((f) => f.name === "land_type"),
              data,
            )}
          {selectFields
            .filter((field) => field.name !== "property_type")
            .map((field) => renderField(field, data))}
        </div>

        <MapComponents
          latitude={latitude}
          longitude={longitude}
          setLatitude={setLatitude}
          setLongitude={setLongitude}
          mapError={mapError}
          setMapError={setMapError}
        />
        <div className={s.row}>
          {inputFields.map((field) => renderField(field, data))}
          {renderDynamicFields()}
        </div>

        <PhotoUploader
          onPhotosChange={setSelectedPhotos}
          photoError={photoError}
          setPhotoError={setPhotoError}
        />
        <div className="centerLoading">
          {loading ? (
            <div className="loader"></div>
          ) : (
            <button type="submit" className="saveButton">
              Добавить объект
            </button>
          )}
        </div>
      </div>
    </form>
  );
};

export default AddObject;
